import React from "react";

import { POIAreaIcon } from "@/assets/icons";
import Divider from "@/components/ui/Divider";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { DatasetPoiParameter } from "@/types/poiParameter";

import { DatasetItem } from "./DatasetItem";

interface DatasetSectionProps {
  title: string;
  description: string;
  datasets: DatasetPoiParameter[] | DatasetPoiParameter[][];
  isLockedSection?: boolean;
  isSelection?: boolean;
  selectedIds?: string[];
  onChange?: (ids: string[]) => void;
  onClickLockIcon?: (dataset: DatasetPoiParameter) => void;
  emptyStateMessage?: string;
  isLoading?: boolean;
}

export const DatasetSection: React.FC<DatasetSectionProps> = ({
  title,
  description,
  datasets,
  isLockedSection = false,
  isSelection,
  selectedIds,
  onChange,
  onClickLockIcon,
  emptyStateMessage,
  isLoading = false
}) => {
  const handleToggle = (id: string) => {
    if (!onChange || !selectedIds) return;

    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];

    onChange(next);
  };

  // Check if datasets is multi-dimensional array
  const isMultiArray = datasets.length > 0 && Array.isArray(datasets[0]);
  const flatDatasets = isMultiArray
    ? (datasets as DatasetPoiParameter[][]).flat()
    : (datasets as DatasetPoiParameter[]);

  const renderEmptyState = (message?: string) => (
    <div className="flex items-center justify-center h-16 bg-white-smoke px-14 rounded-xl gap-4">
      <div className=" w-8 h-8">
        <POIAreaIcon className="fill-light-gray w-8 h-8" />
      </div>
      <div className="text-xs text-txtPlaceholder">
        {message || "No datasets available."}
      </div>
    </div>
  );

  const renderDatasetItems = (items: DatasetPoiParameter[]) =>
    items.map((item) => (
      <DatasetItem
        key={item.dataset_id}
        name={item.dataset_name}
        isLocked={isLockedSection}
        isSelection={isSelection}
        checked={selectedIds?.includes(item.dataset_id)}
        onChange={() => handleToggle(item.dataset_id)}
        onClickLockIcon={() => onClickLockIcon?.(item)}
      />
    ));

  const renderGroupedDatasets = () => (
    <>
      {(datasets as DatasetPoiParameter[][]).map((group, groupIndex) => (
        <React.Fragment key={group[0].parent_id}>
          {renderDatasetItems(group)}
          {groupIndex < (datasets as DatasetPoiParameter[][]).length - 1 && (
            <div className="py-2">
              <Divider className="bg-gainsboro w-full h-[1px]" />
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );

  const renderFlatDatasets = () => (
    <>
      {(datasets as DatasetPoiParameter[]).map((item) => (
        <DatasetItem
          key={item.dataset_id}
          name={item.dataset_name}
          isLocked={isLockedSection}
          isSelection={isSelection}
          checked={selectedIds?.includes(item.dataset_id)}
          onChange={() => handleToggle(item.dataset_id)}
          onClickLockIcon={() => onClickLockIcon?.(item)}
        />
      ))}
    </>
  );

  const renderDatasets = () => {
    if (flatDatasets.length === 0) {
      return renderEmptyState(emptyStateMessage);
    }

    if (isMultiArray) {
      return renderGroupedDatasets();
    }

    return renderFlatDatasets();
  };

  return (
    <div>
      <h3 className="font-semibold text-txtHeading mb-2 font-['Plus_Jakarta_Sans']">
        {title}
      </h3>

      <p className="text-sm leading-5 text-txtBody mb-4 font-['Plus_Jakarta_Sans']">
        {description}
      </p>
      {/* overlay: list stays mounted, so the spinner area follows its height */}
      <LoadingWrapper
        isLoading={isLoading}
        overlay
        loadingClassName="rounded-xl"
      >
        <div className="space-y-2 pr-1">{renderDatasets()}</div>
      </LoadingWrapper>
    </div>
  );
};
